import { useState, useEffect } from "react";
import { Cookie, X, Settings } from "lucide-react";
import { Button } from "../ui/button";

export const CookieConsent = () => {
  const [isVisible, setIsVisible] = useState(false);
  const [showPreferences, setShowPreferences] = useState(false);
  const [preferences, setPreferences] = useState({
    essential: true, // Always true, cannot be disabled
    analytics: false,
    functional: false,
    marketing: false,
  });

  useEffect(() => {
    // Check if user has already made a choice
    const consent = localStorage.getItem("cookie-consent");
    if (!consent) {
      // Small delay for better UX
      const timer = setTimeout(() => setIsVisible(true), 1000);
      return () => clearTimeout(timer);
    }
  }, []);

  const handleAcceptAll = () => {
    const allAccepted = {
      essential: true,
      analytics: true,
      functional: true,
      marketing: true,
    };
    localStorage.setItem("cookie-consent", JSON.stringify(allAccepted));
    setIsVisible(false);
  };

  const handleRejectNonEssential = () => {
    const essentialOnly = {
      essential: true,
      analytics: false,
      functional: false,
      marketing: false,
    };
    localStorage.setItem("cookie-consent", JSON.stringify(essentialOnly));
    setIsVisible(false);
  };

  const handleSavePreferences = () => {
    localStorage.setItem("cookie-consent", JSON.stringify(preferences));
    setIsVisible(false);
    setShowPreferences(false);
  };

  if (!isVisible) return null;

  return (
    <div className="fixed bottom-0 left-0 right-0 z-[60] p-4 md:p-6">
      <div className="max-w-4xl mx-auto">
        <div className="bg-white rounded-2xl shadow-2xl border border-[#E2E8F0] overflow-hidden">
          {!showPreferences ? (
            // Main Banner
            <div className="p-6">
              <div className="flex items-start gap-4">
                <div className="w-12 h-12 rounded-xl bg-[#C8A951]/10 flex items-center justify-center shrink-0">
                  <Cookie className="w-6 h-6 text-[#C8A951]" />
                </div>
                <div className="flex-1">
                  <h3 className="text-lg font-bold text-[#0F172A] mb-2">
                    We Value Your Privacy
                  </h3>
                  <p className="text-sm text-[#64748B] mb-4">
                    We use cookies to enhance your browsing experience, serve personalized content, and analyze our traffic. 
                    By clicking "Accept All", you consent to our use of cookies. Read our{" "}
                    <a href="/cookie-policy" className="text-[#C8A951] hover:underline">Cookie Policy</a> for more information.
                  </p>
                  <div className="flex flex-wrap gap-3">
                    <Button
                      onClick={handleAcceptAll}
                      className="bg-[#C8A951] hover:bg-[#B8993F] text-[#0F172A] font-semibold px-6"
                    >
                      Accept All
                    </Button>
                    <Button
                      onClick={handleRejectNonEssential}
                      variant="outline"
                      className="border-[#E2E8F0] text-[#475569] hover:bg-[#F8FAFC] hover:border-[#C8A951]/50"
                    >
                      Essential Only
                    </Button>
                    <Button
                      onClick={() => setShowPreferences(true)}
                      variant="ghost"
                      className="text-[#64748B] hover:text-[#C8A951]"
                    >
                      <Settings className="w-4 h-4 mr-2" />
                      Manage Preferences
                    </Button>
                  </div>
                </div>
                <button
                  onClick={handleRejectNonEssential}
                  className="text-[#94A3B8] hover:text-[#64748B] transition-colors"
                >
                  <X className="w-5 h-5" />
                </button>
              </div>
            </div>
          ) : (
            // Preferences Panel
            <div className="p-6">
              <div className="flex items-center justify-between mb-6">
                <h3 className="text-lg font-bold text-[#0F172A]">Cookie Preferences</h3>
                <button
                  onClick={() => setShowPreferences(false)}
                  className="text-[#94A3B8] hover:text-[#64748B] transition-colors"
                >
                  <X className="w-5 h-5" />
                </button>
              </div>
              
              <div className="space-y-4 mb-6">
                {/* Essential - Always On */}
                <div className="flex items-center justify-between p-4 bg-[#F8FAFC] rounded-lg">
                  <div>
                    <h4 className="font-semibold text-[#0F172A]">Essential Cookies</h4>
                    <p className="text-sm text-[#64748B]">Required for the website to function. Cannot be disabled.</p>
                  </div>
                  <div className="w-12 h-6 bg-[#C8A951] rounded-full flex items-center justify-end px-1">
                    <div className="w-4 h-4 bg-white rounded-full" />
                  </div>
                </div>
                
                {/* Analytics */}
                <label className="flex items-center justify-between p-4 bg-[#F8FAFC] rounded-lg cursor-pointer">
                  <div>
                    <h4 className="font-semibold text-[#0F172A]">Analytics Cookies</h4>
                    <p className="text-sm text-[#64748B]">Help us understand how visitors interact with our website.</p>
                  </div>
                  <input
                    type="checkbox"
                    checked={preferences.analytics}
                    onChange={(e) => setPreferences(prev => ({ ...prev, analytics: e.target.checked }))}
                    className="w-5 h-5 rounded border-[#E2E8F0] text-[#C8A951] focus:ring-[#C8A951]"
                  />
                </label>
                
                {/* Functional */}
                <label className="flex items-center justify-between p-4 bg-[#F8FAFC] rounded-lg cursor-pointer">
                  <div>
                    <h4 className="font-semibold text-[#0F172A]">Functional Cookies</h4>
                    <p className="text-sm text-[#64748B]">Remember your preferences and provide enhanced features.</p>
                  </div>
                  <input
                    type="checkbox"
                    checked={preferences.functional}
                    onChange={(e) => setPreferences(prev => ({ ...prev, functional: e.target.checked }))}
                    className="w-5 h-5 rounded border-[#E2E8F0] text-[#C8A951] focus:ring-[#C8A951]"
                  />
                </label>
                
                {/* Marketing */}
                <label className="flex items-center justify-between p-4 bg-[#F8FAFC] rounded-lg cursor-pointer">
                  <div>
                    <h4 className="font-semibold text-[#0F172A]">Marketing Cookies</h4>
                    <p className="text-sm text-[#64748B]">Used to display relevant ads and track campaign effectiveness.</p>
                  </div>
                  <input
                    type="checkbox"
                    checked={preferences.marketing}
                    onChange={(e) => setPreferences(prev => ({ ...prev, marketing: e.target.checked }))}
                    className="w-5 h-5 rounded border-[#E2E8F0] text-[#C8A951] focus:ring-[#C8A951]"
                  />
                </label>
              </div>
              
              <div className="flex gap-3">
                <Button
                  onClick={handleSavePreferences}
                  className="bg-[#C8A951] hover:bg-[#B8993F] text-[#0F172A] font-semibold px-6"
                >
                  Save Preferences
                </Button>
                <Button
                  onClick={() => setShowPreferences(false)}
                  variant="outline"
                  className="border-[#E2E8F0] text-[#475569] hover:border-[#C8A951]/50"
                >
                  Cancel
                </Button>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};
